/**
 * Fixture operator profile — the six doors of `acs_service/routers/profile.py`, behaviour copied.
 *
 * Fixture mode never contacts a server, so `createFixtureApi()` has to answer the profile family
 * itself. What makes this a *copy* rather than a second source of truth is that every judgement it
 * publishes is the server's own, ported: the notes are the verbatim strings of `routers/profile.py`
 * and `operator_profile.py`, the validator below is the rule subset of
 * `acs_service/operator_profile.py` ported key-for-key (same rule names, same sentences, same
 * one-report-every-error semantics), and the capacity arithmetic is `capacity_preview`'s formulas
 * with its clamps. A door that changes and a fixture that drifts must therefore become a red test —
 * the E1 in-process half is `web/src/test/profileShapes.test.ts` against the measurement in
 * `fixtures/profile-measured-shapes.json`, written by `tests/test_acs_u16e3_profile_doors.py` from the
 * six real doors on `MemoryStore` (U-16-e3 landed both the artefact and the drift test; the sentence
 * this header used to carry — «no `profile-measured-shapes.json` exists yet» — was the stale half of
 * the hand-off note U-16-e2 wrote).
 *
 * The four lie-rules this file exists to make renderable:
 *  1. **the report is whole**: `validateFixtureProfile` returns EVERY problem in one pass; the doors
 *     publish `problems` verbatim, so a page that dropped one line is visible against the fixture;
 *  2. **activation is immutable and single-document**: the fixture store compare-and-swaps on
 *     `expected_version` and answers a losing race with 409 `profile-stale-version` carrying the
 *     store's own `current_version` read-back; rollback mints a NEW version with `rollback_of` set,
 *     it never rewrites a row;
 *  3. **readiness reports and never decides**: every fleet row the fixture emits carries
 *     `write_authorized: false` by design, and `release_blocked` is derived from judging the stored
 *     document, never asserted;
 *  4. **the history window is bounded**: `FIXTURE_PROFILE_WINDOW_TRIMMED` is the restore case the
 *     server documents — an `active_version` whose row the window no longer carries — and both read
 *     doors answer it with the store's own number under `MISSING_ACTIVE_ROW` sentences, never with a
 *     substitute document.
 *
 * No secret value lives here (§۲:۴۴): `secrets` carries references (`env://…`) and the validator's
 * secret rules refuse without quoting, exactly like the server's.
 */

import type {
  ProfileDiffEntry,
  ProfileDocument,
  ProfileProblemReport,
  ProfileReadinessResponse,
  ProfileValidationReport,
  ProfileVersionRow,
} from '../api/types';

/* ------------------------------------------------------------------ vocabularies --- */

/** `operator_profile.ENVIRONMENTS` (§۲:۳۸), mirrored. */
export const FIXTURE_PROFILE_ENVIRONMENTS = ['lab', 'staging', 'production'] as const;

/** `operator_profile.INTEGRATION_MODES` (§۲:۴۳), mirrored. */
export const FIXTURE_PROFILE_INTEGRATION_MODES = ['disabled', 'mock', 'live'] as const;

/** `operator_profile.INTEGRATION_SYSTEMS` (§۲:۴۳), mirrored. */
export const FIXTURE_PROFILE_INTEGRATION_SYSTEMS = [
  'bss',
  'oss',
  'aaa',
  'ipam',
  'idp',
  'ticketing',
] as const;

/** `operator_profile.TIMEOUT_UNITS` (§۲:۴۰), mirrored. */
export const FIXTURE_PROFILE_TIMEOUT_UNITS = ['s', 'ms'] as const;

/** `operator_profile.SECRET_REF_PATTERN`, mirrored (allowlist schemes, not a generic URI grammar). */
export const FIXTURE_SECRET_REF = /^(env|vault|file):\/\/[A-Za-z0-9][A-Za-z0-9._/-]{2,255}$/;

/** `operator_profile._SECRETISH`, mirrored: a key NAME that promises a credential. */
const SECRETISH = /(password|passwd|secret|token|community|private[_-]?key)/i;

/** `operator_profile.UNMODELED_GROUPS`, mirrored — an extra key hears its owner, not «extra». */
const UNMODELED_GROUPS: Record<string, string> = {
  service: 'S1 (`G-SUB-03`)',
  olt_inventory: 'C11 (`G-OLT-08`)',
  operations: 'P-h / H4 (`G-REL-02`)',
  data: 'H2 (`G-REL-01`)',
  release: 'U-18 (`REQ-SCALE`)',
};

/** `OperatorProfileDocument`'s field set, mirrored; required ones are `identity`/`capacity`/`cwmp_behavior`/`secrets`. */
const DOCUMENT_FIELDS = [
  'identity',
  'capacity',
  'cwmp_behavior',
  'ont_fleet',
  'integrations',
  'features',
  'secrets',
] as const;
const DOCUMENT_REQUIRED = ['identity', 'capacity', 'cwmp_behavior', 'secrets'] as const;

/** Per-section field tables the strict shape pass mirrors (name → required scalar kind). */
const SECTION_FIELDS: Record<
  string,
  { required: Record<string, string>; optional: Record<string, string> }
> = {
  identity: {
    required: { operator_id: 'string', environment: 'string', timezone: 'string' },
    optional: { title: 'string|null' },
  },
  capacity: {
    required: {},
    optional: {
      current_onts: 'int|null',
      target_onts: 'int|null',
      olt_count: 'int|null',
      console_users: 'int|null',
      regions: 'int|null',
      growth_reason: 'string|null',
    },
  },
  cwmp_behavior: {
    required: {},
    optional: {
      inform_interval_seconds: 'int|null',
      inform_jitter_seconds: 'int|null',
      session_max_seconds: 'int|null',
      max_concurrent_sessions: 'int|null',
      cr_budget_per_minute: 'int|null',
      timeouts: 'list',
    },
  },
  secrets: {
    required: {},
    optional: {
      credential_ref: 'string|null',
      signing_key_ref: 'string|null',
      snmp_credential_ref: 'string|null',
    },
  },
};

/* -------------------------------------------------------------------- server notes --- */

/** `routers/profile.py::NOTHING_ACTIVATED_NOTE`, verbatim. */
export const NOTHING_ACTIVATED_NOTE =
  'nothing has been activated on this deployment. `active_version` and `document` are null ' +
  'because no profile exists here yet — not because a version was removed, and there is no ' +
  "version 0. The door that mints the first version is U-06-d's " +
  '`PUT /ops/v1/operator-profile`.';

/** `routers/profile.py::MISSING_ACTIVE_ROW_NOTE`, verbatim — the «شماره هست، سند نیست» answer. */
export const MISSING_ACTIVE_ROW_NOTE =
  "`active_version` names a version this store's history no longer carries, so `document` is " +
  "null: the version number is the store's own and is reported as it stands rather than " +
  'answered with an empty profile.';

/** `routers/profile.py::PROFILE_INTENT_NOTE`, verbatim. */
export const PROFILE_INTENT_NOTE =
  'the active profile document, verbatim: this response is the only export format, so an API ' +
  'export and a UI export are the same bytes. The document is what the deployment declares ' +
  'about itself — intent, and no device state read back from it.';

/** `routers/profile.py::ACTIVATION_INTENT_NOTE`, verbatim. */
export const ACTIVATION_INTENT_NOTE =
  'the candidate is now the active profile version, and that is the whole claim of this response. ' +
  'The profile is intent, not applied state: activation sends nothing to a device, creates no job ' +
  'and writes no preset — it records the ceiling under which later device writes are judged ' +
  '(Derived-3). The version is immutable; the next activation adds a new row instead of editing ' +
  'this one, and `GET /ops/v1/operator-profile` is the only export of its document.';

/** `routers/profile.py::ROLLBACK_INTENT_NOTE`, verbatim — why rollback is forward-only. */
export const ROLLBACK_INTENT_NOTE =
  'the document of the version named by `rollback_of` is active again, as a NEW forward version: no ' +
  'stored version was rewritten, and no device operation already sent is undone. This response ' +
  'records intent — the profile declares what the deployment means from now on, and reversing work a ' +
  'device already received is a separate operation on the fleet, never a settings write ' +
  '(Derived-3).';

/** `routers/profile.py::VERSION_PAGE`, mirrored (the door's page, not the store's window). */
export const VERSION_PAGE = 50;

/** `routers/profile.py::VERSIONS_NOTE`, verbatim. */
export const VERSIONS_NOTE =
  'the activation history, newest first, at most ' +
  `${VERSION_PAGE} rows per answer: \`has_more\` says whether older rows exist beyond this page, and ` +
  'no row carries its document — `GET /ops/v1/operator-profile` is the only export and it exports ' +
  'the active one (§۲:۵۹). `changed_keys` names the top-level sections a version moved against the ' +
  'row before it: for the first version ever that is every section, and for the oldest row of a ' +
  'history the bounded window has already trimmed it is EMPTY, which means «not computable» and not ' +
  '«nothing changed».';

/** `operator_profile.NO_PROFILE_READINESS_NOTE`, verbatim. */
export const NO_PROFILE_READINESS_NOTE =
  'nothing has been activated on this deployment, so there is no profile to judge: ' +
  '`profile_version` and `environment` are null and `fleet` is empty. `release_blocked` is true ' +
  'because an absent profile is not a ready one — not because a rule was broken. The door that ' +
  'mints the first version is `PUT /ops/v1/operator-profile`.';

/** `operator_profile.MISSING_ACTIVE_ROW_READINESS_NOTE`, verbatim. */
export const MISSING_ACTIVE_ROW_READINESS_NOTE =
  "`profile_version` is this store's own `active_version` and is reported as it stands, but no " +
  'document backs it in the history window this build carries, so there is nothing here to judge. ' +
  '`environment` is null because there is no document to read it from and `fleet` is empty for the ' +
  'same reason. `release_blocked` is true because a profile this build cannot read is not a ' +
  'releasable one — and this is not a deployment that never had a profile: a version was activated ' +
  'here, and it is its row that is missing. `GET /ops/v1/operator-profile/versions` answers which ' +
  'versions this store still carries, and the door that produces a row this build can read is ' +
  '`PUT /ops/v1/operator-profile`.';

/** `operator_profile.READINESS_NOTE`, verbatim. */
export const READINESS_NOTE =
  'the stored profile judged against what this build knows — the vendor registry, the authored ' +
  "manifests and the validator's own rules. It reports and decides nothing: every fleet entry " +
  'carries `write_authorized: false` by design, because the witness gate is the only authority ' +
  "that grants a write (§۲:۴۱; circuit M's `capability-unproven`, P-3), never this document. The " +
  'capacity numbers are arithmetic over the declared profile — see `capacity.measured`, which is ' +
  'false. This is intent, not applied device state (Derived-3).';

/** `operator_profile.CAPACITY_BASIS` / `CAPACITY_NOTE`, verbatim. */
export const CAPACITY_BASIS = 'derived from the stored profile';
export const CAPACITY_NOTE =
  'arithmetic over the declared profile: every number is computed from what the operator wrote ' +
  'into `capacity` and `cwmp_behavior`, and none of it is measured against a running deployment. ' +
  'A line whose input is `null` answers `null` and names that input in `unknown_inputs` — it is ' +
  'never `0`. `informs_per_hour = n * (3600 // max(interval, 1))`; `peak_informs_per_minute` divides ' +
  'by `max(interval - jitter, 1)` (worst-case compression) and rounds up; `session_slots` is the peak times ' +
  "`session_max_seconds` over ۶۰, rounded up; `cr_budget_per_minute` is the profile's own value. " +
  'Measuring real offered load is U-00/E2 and U-18.';

/* ------------------------------------------------------------------ the fixture docs --- */

/** `store.PROFILE_VERSION_HISTORY`, mirrored: the window the store keeps rows in. */
export const FIXTURE_PROFILE_VERSION_HISTORY = 100;

/** A blessed lab-profile: references only, nulls where the operator declared nothing. */
function fixtureProfileDoc(environment: string, currentOnts: number | null): ProfileDocument {
  return {
    identity: {
      operator_id: 'demo-isp',
      environment,
      timezone: 'Asia/Tehran',
      title: 'پروفایل عملیات demo',
    },
    capacity: {
      current_onts: currentOnts,
      target_onts: currentOnts === null ? null : currentOnts + 500,
      olt_count: 12,
      console_users: 30,
      regions: 4,
      growth_reason: null,
    },
    cwmp_behavior: {
      inform_interval_seconds: 300,
      inform_jitter_seconds: 60,
      session_max_seconds: 120,
      max_concurrent_sessions: 1200,
      cr_budget_per_minute: 90,
      timeouts: [
        { operation: 'get_parameter_values', value: 30, unit: 's' },
        { operation: 'set_parameter_values', value: 60, unit: 's' },
      ],
    },
    ont_fleet: [
      {
        vendor: 'Huawei',
        oui: '485754',
        product_class: null,
        model: 'EG8145V5',
        hardware_version: null,
        firmware_version: 'V3R019C10S160',
        count: 3200,
        share_percent: null,
      },
      {
        vendor: 'ZTE',
        oui: null,
        product_class: 'ZTEF670LV9',
        model: 'F670L',
        hardware_version: null,
        firmware_version: null,
        count: 900,
        share_percent: null,
      },
    ],
    integrations: [
      {
        system: 'bss',
        mode: 'live',
        base_url: 'https://bss.demo-isp.invalid/api',
        timeout_ms: 5000,
        schema_version: null,
        scope: ['subscriber.read', 'subscriber.write'],
      },
      {
        system: 'aaa',
        mode: 'disabled',
        base_url: null,
        timeout_ms: null,
        schema_version: null,
        scope: [],
      },
    ],
    features: [{ name: 'self_install', requires: ['bss'] }],
    secrets: {
      credential_ref: 'env://ACS_DEMO_CREDENTIAL_REF',
      signing_key_ref: null,
      snmp_credential_ref: 'vault://snmp/demo-community',
    },
  };
}

/** One history row — the shape `store.bump_profile_version` keeps. */
export interface FixtureProfileVersionRow {
  version: number;
  activated_at: string;
  actor: { kind: string; subject: string };
  document: ProfileDocument;
}

/** The fixture's stored profile family — the `acs_meta` copy `store.get_operator_profile()` returns. */
export interface FixtureProfileState {
  active_version: number | null;
  versions: FixtureProfileVersionRow[];
}

const DAY = 86_400_000;
const ANCHOR = Date.parse('2026-10-17T06:00:00Z');
const iso = (offsetDays: number): string =>
  new Date(ANCHOR - offsetDays * DAY).toISOString().replace(/\.\d{3}Z$/, 'Z');

/**
 * The demo deployment: three activations, the newest active. Two humans and one machine left rows
 * behind — the nested actor of shape (ج) renders the machine subject as a client id, not a person,
 * exactly as `routers/profile.py::_actor` writes it (G-IDN-05 is still open).
 */
export const FIXTURE_PROFILE_STATE: FixtureProfileState = {
  active_version: 3,
  versions: [
    {
      version: 1,
      activated_at: iso(46),
      actor: { kind: 'human', subject: 'owner-demo' },
      document: fixtureProfileDoc('lab', 3200),
    },
    {
      version: 2,
      activated_at: iso(12),
      actor: { kind: 'machine', subject: 'client:bss-sync' },
      document: fixtureProfileDoc('lab', 3600),
    },
    {
      version: 3,
      activated_at: iso(2),
      actor: { kind: 'human', subject: 'admin-demo' },
      document: fixtureProfileDoc('staging', 4100),
    },
  ],
};

/**
 * The bounded-window case, seeded as a restore would leave it: the store's own `active_version`
 * names 104, the window carries rows 102–103 only. The read doors answer «شماره هست، سند نیست» —
 * the version number stands, the document is null, the note says which fact this is, and no
 * substitute document is ever rendered.
 */
export const FIXTURE_PROFILE_WINDOW_TRIMMED: FixtureProfileState = {
  active_version: 104,
  versions: [
    {
      version: 102,
      activated_at: iso(30),
      actor: { kind: 'human', subject: 'owner-demo' },
      document: fixtureProfileDoc('lab', 2400),
    },
    {
      version: 103,
      activated_at: iso(9),
      actor: { kind: 'human', subject: 'admin-demo' },
      document: fixtureProfileDoc('lab', 2600),
    },
  ],
};

/** A deployment that never saw `PUT` at all — the null pair plus `NOTHING_ACTIVATED_NOTE`. */
export const FIXTURE_PROFILE_EMPTY: FixtureProfileState = { active_version: null, versions: [] };

export function cloneProfileState(state: FixtureProfileState): FixtureProfileState {
  return {
    active_version: state.active_version,
    versions: state.versions.map((row) => ({
      ...row,
      actor: { ...row.actor },
      document: { ...row.document },
    })),
  };
}

/* ------------------------------------------------------------------ the mini validator --- */

/** Port of `operator_profile._walk`: every (dotted path, key, value), children key-sorted. */
function walk(payload: unknown, path = ''): [string, string, unknown][] {
  const out: [string, string, unknown][] = [];
  if (Array.isArray(payload)) {
    payload.forEach((item, index) => out.push(...walk(item, `${path}[${index}]`)));
  } else if (payload !== null && typeof payload === 'object') {
    for (const key of Object.keys(payload as object).sort()) {
      const child = path ? `${path}.${key}` : key;
      const value = (payload as Record<string, unknown>)[key];
      out.push([child, key, value]);
      out.push(...walk(value, child));
    }
  }
  return out;
}

function problem(path: string, rule: string, detail: string): ProfileProblemReport {
  return { path, rule, detail };
}

function typeWords(kind: string): string {
  return (
    {
      string: 'a string',
      int: 'an integer',
      bool: 'true or false',
      list: 'a list',
      object: 'an object',
    }[kind] ?? `a ${kind}`
  );
}

/** Strict scalar check with the server's rule names: `type`, never coercion (`"1200"` fails). */
function scalarProblem(path: string, value: unknown, want: string): ProfileProblemReport | null {
  const nullOk = want.endsWith('|null');
  const kind = nullOk ? want.slice(0, want.indexOf('|')) : want;
  if (value === null) return nullOk ? null : problem(path, 'type', `must be ${typeWords(kind)}`);
  if (kind === 'int')
    return Number.isInteger(value) ? null : problem(path, 'type', 'must be an integer');
  if (kind === 'string')
    return typeof value === 'string' ? null : problem(path, 'type', 'must be a string');
  return null;
}

/**
 * The ported validator. Returns `[document|null, problems]` exactly like
 * `validate_profile_document`: a document comes back only when the report is EMPTY, so a candidate
 * whose only complaints are `release-*` rules arrives as `(null, [release-*])` and the doors report
 * `valid: false, release_blocked: true` — never a blessed dump with a complaint beside it.
 *
 * Ported rules: the raw-body secret sweep (never quoting the value), document shape (missing-field /
 * unknown-field with the §۲ group owners named), section shape (strict types, extra forbidden),
 * environment-unknown, reason-required, jitter-exceeds-interval, timeout-unit, duplicate-operation,
 * the integration vocabularies, duplicate-system, dependency-disabled, secret-ref-shape, and the two
 * release rules. The bounds (`ge`/`le`) and the `operator_id` pattern are NOT ported — a fixture
 * that half-copies a schema is a second voice; the schema itself stays in Python.
 */
export function validateFixtureProfile(
  payload: unknown,
): [ProfileDocument | null, ProfileProblemReport[]] {
  const sweep = walk(payload)
    .filter(
      ([, key, value]) => typeof value === 'string' && SECRETISH.test(key) && !key.endsWith('_ref'),
    )
    .map(([path, key]) =>
      problem(
        path,
        'secret-literal',
        `${key} looks like a secret and carries a literal value; §۲:۴۴ admits only a reference ` +
          '(the value itself is not repeated here)',
      ),
    );

  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return [null, [...sweep, problem('', 'shape', 'the candidate profile must be a JSON object')]];
  }
  const doc = payload as ProfileDocument;

  const shape: ProfileProblemReport[] = [];
  for (const key of Object.keys(doc).sort()) {
    if (!(DOCUMENT_FIELDS as readonly string[]).includes(key)) {
      const owner = UNMODELED_GROUPS[key];
      shape.push(
        problem(
          key,
          'unknown-field',
          owner
            ? `\`${key}\` is one of §۲'s input groups that U-06 does not model; its owner is ` +
                `${owner}. It is refused here rather than stored, so the roadmap entry stays the ` +
                'place that decides its shape'
            : `\`${key}\` is not a field of the operator profile contract; the profile is strict, ` +
                'and an unmodelled field that was stored would be read back as a default by a later unit',
        ),
      );
    }
  }
  for (const name of DOCUMENT_REQUIRED) {
    if (!(name in doc)) {
      shape.push(
        problem(
          name,
          'missing-field',
          'this section is part of the profile contract and has to be present, even when every ' +
            'field inside it is unknown',
        ),
      );
    }
  }

  const parsed: Record<string, unknown> = {};
  for (const name of Object.keys(SECTION_FIELDS)) {
    const section = doc[name];
    if (section === undefined) continue;
    if (section === null || typeof section !== 'object' || Array.isArray(section)) {
      shape.push(problem(name, 'type', `must be ${typeWords('object')}`));
      continue;
    }
    parsed[name] = section;
    const table = SECTION_FIELDS[name] as {
      required: Record<string, string>;
      optional: Record<string, string>;
    };
    for (const [key, value] of Object.entries(section as Record<string, unknown>)) {
      const want = table.required[key] ?? table.optional[key];
      if (want === undefined) {
        shape.push(
          problem(
            `${name}.${key}`,
            'unknown-field',
            `\`${key}\` is not a field of the operator profile contract; the profile is strict, ` +
              'and an unmodelled field that was stored would be read back as a default by a later unit',
          ),
        );
        continue;
      }
      if (want === 'list') {
        if (!Array.isArray(value)) shape.push(problem(`${name}.${key}`, 'type', 'must be a list'));
        continue;
      }
      const p = scalarProblem(`${name}.${key}`, value, want);
      if (p) shape.push(p);
    }
    for (const key of Object.keys(table.required)) {
      if (!(key in (section as object))) {
        shape.push(
          problem(
            `${name}.${key}`,
            'missing-field',
            'this section is part of the profile contract and has to be present, even when every ' +
              'field inside it is unknown',
          ),
        );
      }
    }
  }

  // The list sections: entries are checked only for the fields the rules below read.
  const identity = parsed.identity as Record<string, unknown> | undefined;
  const environment = typeof identity?.environment === 'string' ? identity.environment : null;
  const integrations = Array.isArray(doc.integrations)
    ? (doc.integrations as ProfileDocument[])
    : [];
  const features = Array.isArray(doc.features) ? (doc.features as ProfileDocument[]) : [];
  const cwmp = parsed.cwmp_behavior as Record<string, unknown> | undefined;
  const timeouts = Array.isArray(cwmp?.timeouts) ? (cwmp?.timeouts as ProfileDocument[]) : [];

  const rules: ProfileProblemReport[] = [];
  if (identity && !shape.some((p) => p.path.startsWith('identity.'))) {
    if (environment !== null && !FIXTURE_PROFILE_ENVIRONMENTS.includes(environment as never)) {
      rules.push(
        problem(
          'identity.environment',
          'environment-unknown',
          `\`${environment}\` is not an environment; §۲:۳۸ admits ` +
            `${FIXTURE_PROFILE_ENVIRONMENTS.join(' or ')}, and the readiness report is worded ` +
            'differently for each',
        ),
      );
    }
  }
  const capacity = parsed.capacity as Record<string, unknown> | undefined;
  if (capacity) {
    const current = capacity.current_onts;
    const target = capacity.target_onts;
    const reason = capacity.growth_reason;
    if (
      typeof current === 'number' &&
      typeof target === 'number' &&
      target < current &&
      !(typeof reason === 'string' && reason.length > 0)
    ) {
      rules.push(
        problem(
          'capacity.growth_reason',
          'reason-required',
          `target_onts (${target}) is below current_onts (${current}), which says the fleet is ` +
            'shrinking; §۲:۳۹ wants the reason written down before the number is believed',
        ),
      );
    }
  }
  if (cwmp) {
    const interval = cwmp.inform_interval_seconds;
    const jitter = cwmp.inform_jitter_seconds;
    if (typeof interval === 'number' && typeof jitter === 'number' && jitter > interval) {
      rules.push(
        problem(
          'cwmp_behavior.inform_jitter_seconds',
          'jitter-exceeds-interval',
          `jitter ${jitter}s is larger than the inform interval ${interval}s, which spreads the ` +
            'retries over more time than the interval itself and makes the load model meaningless',
        ),
      );
    }
  }
  const seenOps = new Set<string>();
  for (let index = 0; index < timeouts.length; index += 1) {
    const timeout = timeouts[index] as ProfileDocument;
    const unit = timeout.unit;
    if (
      !(FIXTURE_PROFILE_TIMEOUT_UNITS as readonly (string | null | undefined)[]).includes(
        unit as never,
      )
    ) {
      const reason =
        unit === undefined || unit === null
          ? 'has no unit at all'
          : `carries the unit \`${unit}\`, which is not declared`;
      rules.push(
        problem(
          `cwmp_behavior.timeouts[${index}].unit`,
          'timeout-unit',
          `a timeout of \`${timeout.operation}\` ${reason}; §۲:۴۰ requires an explicit unit of ` +
            `${FIXTURE_PROFILE_TIMEOUT_UNITS.join(', ')} — a bare number is read as seconds by one ` +
            'operator and milliseconds by the next',
        ),
      );
    }
    if (seenOps.has(String(timeout.operation))) {
      rules.push(
        problem(
          `cwmp_behavior.timeouts[${index}].operation`,
          'duplicate-operation',
          `\`${timeout.operation}\` is declared more than once; two answers to one operation is not ` +
            'a range, it is a coin toss',
        ),
      );
    }
    seenOps.add(String(timeout.operation));
  }
  const seenSystems = new Set<string>();
  for (let index = 0; index < integrations.length; index += 1) {
    const integration = integrations[index] as ProfileDocument;
    const system = integration.system;
    const mode = integration.mode;
    if (!(FIXTURE_PROFILE_INTEGRATION_SYSTEMS as readonly string[]).includes(String(system))) {
      rules.push(
        problem(
          `integrations[${index}].system`,
          'mode-unknown',
          `\`${system}\` is not an integration this profile may declare; §۲:۴۳ names ` +
            FIXTURE_PROFILE_INTEGRATION_SYSTEMS.join(', '),
        ),
      );
    }
    if (!(FIXTURE_PROFILE_INTEGRATION_MODES as readonly string[]).includes(String(mode))) {
      rules.push(
        problem(
          `integrations[${index}].mode`,
          'mode-unknown',
          `\`${mode}\` is not an integration mode; §۲:۴۳ admits ` +
            `${FIXTURE_PROFILE_INTEGRATION_MODES.join(' or ')}, and each of them answers a ` +
            'different question about what the deployment actually touches',
        ),
      );
    }
    if (seenSystems.has(String(system))) {
      rules.push(
        problem(
          `integrations[${index}].system`,
          'duplicate-system',
          `\`${system}\` is declared twice; one mode per system is what a feature's dependency and ` +
            'a release gate both read',
        ),
      );
    }
    seenSystems.add(String(system));
    if (environment === 'production' && mode === 'mock') {
      rules.push(
        problem(
          `integrations[${index}].mode`,
          'release-mock-in-production',
          `\`${system}\` is \`mock\` while the environment is \`production\`; a profile that admits ` +
            'to pretending may not be activated as the real one',
        ),
      );
    }
  }
  const declared = new Set(integrations.map((i) => String(i.system)));
  const disabled = new Set(
    integrations.filter((i) => i.mode === 'disabled').map((i) => String(i.system)),
  );
  for (let index = 0; index < features.length; index += 1) {
    const feature = features[index] as ProfileDocument;
    for (const requirement of Array.isArray(feature.requires) ? feature.requires : []) {
      if (disabled.has(String(requirement))) {
        rules.push(
          problem(
            `features[${index}].requires`,
            'dependency-disabled',
            `\`${feature.name}\` requires \`${requirement}\`, whose integration is declared ` +
              '`disabled`; §۲ says the feature stays off rather than reaching a system the profile ' +
              'just said it does not use',
          ),
        );
      } else if (!declared.has(String(requirement))) {
        rules.push(
          problem(
            `features[${index}].requires`,
            'dependency-disabled',
            `\`${feature.name}\` requires \`${requirement}\`, which the profile does not declare ` +
              'as an integration at all',
          ),
        );
      }
    }
  }
  const secrets = parsed.secrets as Record<string, unknown> | undefined;
  const refs = ['credential_ref', 'signing_key_ref', 'snmp_credential_ref'];
  if (secrets) {
    for (const key of refs) {
      const value = secrets[key];
      if (typeof value === 'string' && !FIXTURE_SECRET_REF.test(value)) {
        rules.push(
          problem(
            `secrets.${key}`,
            'secret-ref-shape',
            `\`${key}\` must be a reference into the secret store of the form \`env://…\`, ` +
              '`vault://…` or `file://…`, e.g. `env://AAA_CREDENTIAL_REF` (the value is not repeated here)',
          ),
        );
      }
    }
    if (
      environment === 'production' &&
      refs.every((key) => secrets[key] === undefined || secrets[key] === null)
    ) {
      rules.push(
        problem(
          'secrets',
          'release-secret-missing',
          'a production profile declares no secret reference at all; §۲:۴۴ expects a reference ' +
            'even when the value lives somewhere this document may not name',
        ),
      );
    }
  } else if (parsed.secrets === undefined && 'secrets' in doc && doc.secrets !== undefined) {
    // The unparsed-section fallback: a pasted credential in a broken `secrets` block must not drop
    // out of the report because the section itself failed (`_unparsed_secrets_ref_problems`).
    const raw = doc.secrets;
    if (raw !== null && typeof raw === 'object') {
      for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
        if (key.endsWith('_ref') && typeof value === 'string' && !FIXTURE_SECRET_REF.test(value)) {
          rules.push(
            problem(
              `secrets.${key}`,
              'secret-ref-shape',
              `\`${key}\` must be a reference into the secret store of the form \`env://…\`, ` +
                '`vault://…` or `file://…`, e.g. `env://AAA_CREDENTIAL_REF` (the value is not repeated here)',
            ),
          );
        }
      }
    }
  }

  const problems = [...sweep, ...shape, ...rules];
  if (problems.length > 0) return [null, problems];
  return [canonicalizeFixtureProfile(doc), []];
}

/** The fixture's stand-in for `model_dump()`: every section present, defaults filled. */
export function canonicalizeFixtureProfile(doc: ProfileDocument): ProfileDocument {
  return {
    identity: { title: null, ...(doc.identity as object) },
    capacity: {
      current_onts: null,
      target_onts: null,
      olt_count: null,
      console_users: null,
      regions: null,
      growth_reason: null,
      ...(doc.capacity as object),
    },
    cwmp_behavior: {
      inform_interval_seconds: null,
      inform_jitter_seconds: null,
      session_max_seconds: null,
      max_concurrent_sessions: null,
      cr_budget_per_minute: null,
      timeouts: [],
      ...(doc.cwmp_behavior as object),
    },
    ont_fleet: doc.ont_fleet ?? [],
    integrations: doc.integrations ?? [],
    features: doc.features ?? [],
    secrets: {
      credential_ref: null,
      signing_key_ref: null,
      snmp_credential_ref: null,
      ...(doc.secrets as object),
    },
  };
}

/** `operator_profile.release_blocking`, ported: the `release-*` subset of one report. */
export function fixtureReleaseBlocking(problems: ProfileProblemReport[]): ProfileProblemReport[] {
  return problems.filter((p) => p.rule.startsWith('release-'));
}

/* Port of `operator_profile._flatten` / `diff_profile`, same granularity (§۲ گام ۳). */
function flatten(value: unknown, path = ''): Record<string, unknown> {
  if (Array.isArray(value)) {
    const out: Record<string, unknown> = {};
    value.forEach((item, index) => {
      if (
        item !== null &&
        typeof item === 'object' &&
        (Array.isArray(item) ? item.length > 0 : Object.keys(item).length > 0)
      ) {
        Object.assign(out, flatten(item, `${path}[${index}]`));
      } else {
        out[`${path}[${index}]`] = item;
      }
    });
    return out;
  }
  if (value !== null && typeof value === 'object' && Object.keys(value).length > 0) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value as object).sort()) {
      Object.assign(
        out,
        flatten((value as Record<string, unknown>)[key], path ? `${path}.${key}` : key),
      );
    }
    return out;
  }
  return { [path]: value };
}

export function diffFixtureProfile(
  active: ProfileDocument | null,
  candidate: ProfileDocument,
): ProfileDiffEntry[] {
  const before = active ? flatten(active) : {};
  const after = flatten(candidate);
  const paths = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  return paths
    .filter((p) => JSON.stringify(before[p]) !== JSON.stringify(after[p]))
    .map((p) => ({
      path: p,
      ...(Object.prototype.hasOwnProperty.call(before, p) ? { before: before[p] } : {}),
      ...(Object.prototype.hasOwnProperty.call(after, p) ? { after: after[p] } : {}),
    }));
}

/* ------------------------------------------------------------- the store-side answers --- */

/** The `_active` pairing of `routers/profile.py`, ported: a number with no row behind it is `null` with the number kept. */
export function fixtureActivePair(
  state: FixtureProfileState,
): [number | null, ProfileDocument | null] {
  const version = state.active_version;
  if (version === null || version === undefined) return [null, null];
  for (let index = state.versions.length - 1; index >= 0; index -= 1) {
    const row = state.versions[index] as FixtureProfileVersionRow;
    if (row.version === version) return [version, row.document];
  }
  return [version, null];
}

/** One validation + one diff against the stored profile — the `_report` of the router, ported. */
export function fixtureValidationReport(
  state: FixtureProfileState,
  payload: unknown,
): ProfileValidationReport {
  const [document, found] = validateFixtureProfile(payload);
  const [version, active] = fixtureActivePair(state);
  if (document === null) {
    const blocked = fixtureReleaseBlocking(found);
    return {
      valid: false,
      release_blocked: blocked.length > 0 && JSON.stringify(blocked) === JSON.stringify(found),
      problems: found,
      diff: [],
      base_version: version,
    };
  }
  return {
    valid: true,
    release_blocked: false,
    problems: [],
    diff: diffFixtureProfile(active, document),
    base_version: version,
  };
}

/** Port of `capacity_preview` — same formulas, same clamps, same honest nulls. */
export function fixtureCapacityPreview(
  capacity: ProfileDocument,
  cwmp: ProfileDocument,
): Record<string, unknown> {
  const current = (capacity.current_onts as number | null | undefined) ?? null;
  const interval = (cwmp.inform_interval_seconds as number | null | undefined) ?? null;
  const jitter = (cwmp.inform_jitter_seconds as number | null | undefined) ?? null;
  const sessionMax = (cwmp.session_max_seconds as number | null | undefined) ?? null;
  const crBudget = (cwmp.cr_budget_per_minute as number | null | undefined) ?? null;
  const unknownInputs = (
    [
      ['current_onts', current],
      ['inform_interval_seconds', interval],
      ['inform_jitter_seconds', jitter],
      ['session_max_seconds', sessionMax],
      ['cr_budget_per_minute', crBudget],
    ] as [string, number | null][]
  )
    .filter(([, value]) => value === null)
    .map(([name]) => name);
  const ceilDiv = (a: number, b: number): number => Math.ceil(a / b);
  const informs =
    typeof current === 'number' && typeof interval === 'number'
      ? current * Math.floor(3600 / Math.max(interval, 1))
      : null;
  const peak =
    typeof current === 'number' && typeof interval === 'number' && typeof jitter === 'number'
      ? ceilDiv(current * 60, Math.max(interval - jitter, 1))
      : null;
  const slots =
    peak !== null && typeof sessionMax === 'number' ? ceilDiv(peak * sessionMax, 60) : null;
  return {
    informs_per_hour: informs,
    peak_informs_per_minute: peak,
    session_slots: slots,
    cr_budget_per_minute: crBudget,
    unknown_inputs: unknownInputs,
    basis: CAPACITY_BASIS,
    measured: false,
    note: CAPACITY_NOTE,
  };
}

/**
 * The fleet rows of `readiness_report` — ported keys with the invariant that decides the page:
 * `write_authorized` is `false` for every row, always, and that is the rule, not a stub (§۲:۴۱;
 * the witness gate is the only authority that grants a write, and this door never reads it).
 * The registry ruling (`resolve_identity`) is NOT ported — its `reason_fa` belongs to the Python
 * half; the fixture values below are demo data pinned to the fixture fleet above.
 */
function fixtureFleetStatus(entries: ProfileDocument[]): Record<string, unknown>[] {
  return entries.map((entry) => ({
    vendor: entry.vendor ?? null,
    model: entry.model ?? null,
    firmware_version: entry.firmware_version ?? null,
    support_class: entry.model ? 'demo' : 'unknown',
    matched_on: entry.vendor ? 'vendor' : null,
    registry_reason_fa: entry.model
      ? 'پروفایل اعلام می‌کند؛ شاهدِ مدار M اینجا نیست (write_authorized همیشه false).'
      : 'هیچ مدلی اعلام نشده؛ رجیستری چیزی برای داوری ندارد.',
    manifests_authored: entry.model ? 1 : 0,
    firmware_witnessed: false,
    write_authorized: false,
  }));
}

/** `readiness_report(document, (), profile_version)`, ported — including the two document-less states. */
export function fixtureReadiness(
  document: ProfileDocument | null,
  profileVersion: number | null,
): ProfileReadinessResponse {
  if (document === null || Object.keys(document).length === 0) {
    const capacity = fixtureCapacityPreview({}, {});
    return {
      profile_version: profileVersion,
      environment: null,
      release_blocked: true,
      unknown_inputs: [...(capacity.unknown_inputs as string[])],
      unresolved_dependencies: [],
      fleet: [],
      capacity,
      note: profileVersion === null ? NO_PROFILE_READINESS_NOTE : MISSING_ACTIVE_ROW_READINESS_NOTE,
    };
  }
  const [, found] = validateFixtureProfile(document);
  const entries = Array.isArray(document.ont_fleet)
    ? (document.ont_fleet as ProfileDocument[])
    : [];
  const capacity = fixtureCapacityPreview(
    (document.capacity as ProfileDocument) ?? {},
    (document.cwmp_behavior as ProfileDocument) ?? {},
  );
  const unknownInputs = [...(capacity.unknown_inputs as string[])];
  entries.forEach((entry, index) => {
    if (entry.firmware_version === null || entry.firmware_version === undefined) {
      unknownInputs.push(`ont_fleet[${index}].firmware_version`);
    }
  });
  const identity = document.identity as ProfileDocument | undefined;
  return {
    profile_version: profileVersion,
    environment: typeof identity?.environment === 'string' ? identity.environment : null,
    release_blocked: fixtureReleaseBlocking(found).length > 0,
    unknown_inputs: unknownInputs,
    unresolved_dependencies: found
      .filter((p) => p.rule === 'dependency-disabled')
      .map((p) => p.detail),
    fleet: fixtureFleetStatus(entries),
    capacity,
    note: READINESS_NOTE,
  };
}

/** `ProfileVersionRow`s newest first with the `changed_keys` reduce — the door's own algorithm. */
export function fixtureVersionRows(state: FixtureProfileState): {
  rows: ProfileVersionRow[];
  hasMore: boolean;
} {
  const rows = state.versions;
  const newestFirst = [...rows].reverse().slice(0, VERSION_PAGE);
  return {
    rows: newestFirst.map((row, offset) => {
      const position = rows.length - 1 - offset;
      let changed: string[];
      if (position > 0) {
        const prev = rows[position - 1];
        changed = prev === undefined ? [] : changedSections(prev.document, row.document);
      } else if (row.version === 1) {
        changed = changedSections(null, row.document);
      } else {
        // The predecessor left the bounded window: `[]` means «not computable», and VERSIONS_NOTE
        // says so in the payload — filling it with every section would claim a whole new deployment.
        changed = [];
      }
      return {
        version: row.version,
        activated_at: row.activated_at,
        actor: { kind: row.actor.kind, subject: row.actor.subject },
        changed_keys: changed,
      };
    }),
    hasMore: rows.length > newestFirst.length,
  };
}

/** `_changed_sections`, ported: leaf paths reduced to their first segment, sorted. */
export function changedSections(
  previous: ProfileDocument | null,
  document: ProfileDocument,
): string[] {
  const sections = new Set<string>();
  for (const entry of diffFixtureProfile(previous, canonicalizeFixtureProfile(document))) {
    const head = entry.path.split('.', 1)[0]?.split('[', 1)[0] ?? '';
    if (head) sections.add(head);
  }
  return [...sections].sort();
}
